import { PlusOutlined } from '@ant-design/icons'
import { Button, FormItem, Heading, Input, TextArea } from '@lmc/ui-kit'
import {
  DatePicker as AntdDatePicker,
  Form as AntdForm,
  Divider,
  Modal
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { NAMESPACE } from 'locale'
import { procedureConstants } from 'locale/procedures'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useProcedureCreate } from '../hooks/useProcedureCreate'
import { useProcedureUpdate } from '../hooks/useProcedureUpdate'
import type {
  CreateProcedureInput,
  Procedure,
  UpdateProcedureInput
} from '../types'

dayjs.extend(utc)

export type ProcedureModalProps = {
  isVisible: boolean
  onClose: () => void
  onSuccess: () => void
  procedure?: Procedure
  residentId: string
}

const ProcedureModal = ({
  isVisible,
  onClose,
  onSuccess,
  procedure,
  residentId
}: ProcedureModalProps) => {
  const { t } = useTranslation(NAMESPACE.PROCEDURES)
  const [form] = AntdForm.useForm()
  const isEditMode = !!procedure

  const { createProcedure, isCreating } = useProcedureCreate(() => {
    onSuccess()
    onClose()
  })

  const { updateProcedure, isUpdating } = useProcedureUpdate(residentId, () => {
    onSuccess()
    onClose()
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (isVisible) {
      if (procedure) {
        // Parse UTC datetime and convert to local time for display
        const dateTimeValue = procedure.procedureDateTime
          ? dayjs.utc(procedure.procedureDateTime).local()
          : undefined

        form.setFieldsValue({
          title: procedure.title || '',
          description: procedure.description || '',
          procedureDateTime: dateTimeValue
        })
      } else {
        form.resetFields()
      }
    }
  }, [isVisible, procedure, form])

  const handleSubmit = async (values: unknown) => {
    const formValues = values as {
      title: string
      description?: string
      procedureDateTime?: Dayjs | null
    }

    const input: CreateProcedureInput = {
      title: formValues.title,
      description: formValues.description,
      // Explicitly handle null to allow clearing the field
      procedureDateTime: formValues.procedureDateTime
        ? formValues.procedureDateTime.toISOString()
        : formValues.procedureDateTime === null
          ? null
          : undefined
    }

    // Remove undefined values but keep null values (null explicitly clears the field)
    const cleanedSubmitValues = Object.fromEntries(
      Object.entries(input).filter(([_, value]) => value !== undefined)
    ) as typeof input

    if (isEditMode && procedure) {
      const updateInput: UpdateProcedureInput = {
        _id: procedure._id,
        ...cleanedSubmitValues
      }
      await updateProcedure(updateInput)
    } else {
      await createProcedure(cleanedSubmitValues, residentId)
    }
  }

  if (!isVisible) return null

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      destroyOnClose
      footer={
        <ModalFooter>
          <Button onClick={onClose}>{t(procedureConstants.CANCEL)}</Button>
          <Button
            onClick={form.submit}
            variant="primary"
            isLoading={isEditMode ? isUpdating : isCreating}
          >
            {isEditMode ? (
              t(procedureConstants.SAVE)
            ) : (
              <>
                <PlusOutlined />
                {t(procedureConstants.ADD)}
              </>
            )}
          </Button>
        </ModalFooter>
      }
      width={600}
    >
      <Heading level={3}>{t(procedureConstants.PROCEDURE_RECORD)}</Heading>
      <Divider />
      <StyledForm
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        key={procedure?._id || 'new'}
      >
        <FormItem
          name="title"
          label={t(procedureConstants.TITLE)}
          rules={[
            {
              required: true,
              message: t(procedureConstants.TITLE_REQUIRED)
            }
          ]}
        >
          <Input
            maxLength={60}
            showCount
            placeholder={t(procedureConstants.TITLE_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem
          name="description"
          label={t(procedureConstants.DESCRIPTION_OF_PROCEDURE)}
        >
          <TextArea
            rows={4}
            placeholder={t(procedureConstants.DESCRIPTION_PLACEHOLDER)}
          />
        </FormItem>

        <FormItem
          name="procedureDateTime"
          label={t(procedureConstants.DATE_OF_PROCEDURE)}
        >
          <AntdDatePicker
            showTime={{ format: 'HH:mm' }}
            format="DD-MM-YYYY HH:mm"
            style={{ width: '100%' }}
            placeholder={t(procedureConstants.DATE_TIME_PLACEHOLDER)}
          />
        </FormItem>
        <Divider />
      </StyledForm>
    </Modal>
  )
}

const StyledForm = styled(AntdForm)`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

export default ProcedureModal
